import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getMyAssessmentResult, GetMyAssessmentResultOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, XCircle, Eye, Camera, MessageSquare, Mic, FileText, Download, ShieldAlert, User, Calendar } from 'lucide-react';
import { format } from 'date-fns';

type ResultData = GetMyAssessmentResultOutputType;

export default function MyAssessmentResult() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user, isLoading, loginWithRedirect } = useAuth();
  const [data, setData] = useState<ResultData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { if (!isLoading && !user) loginWithRedirect(); }, [isLoading, user, loginWithRedirect]);

  useEffect(() => {
    if (!user || !sessionId) return;
    getMyAssessmentResult({ sessionId })
      .then(setData)
      .catch(e => setError(e?.message || 'Could not load result'))
      .finally(() => setLoading(false));
  }, [user, sessionId]);

  if (isLoading || !user) return null;

  if (loading) return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
      <Skeleton className="h-8 w-48" /><Skeleton className="h-20" /><Skeleton className="h-40" /><Skeleton className="h-40" />
    </div>
  );

  if (error || !data) return (
    <div className="max-w-lg mx-auto px-4 py-12 text-center">
      <ShieldAlert className="w-10 h-10 text-destructive mx-auto mb-3" />
      <p className="text-sm text-muted-foreground mb-4">{error || 'Result not found'}</p>
      <Link to="/train" className="text-sm text-primary hover:underline">← Back to Training</Link>
    </div>
  );

  const isApproved = data.session.status === 'Approved';
  const typeIcons: Record<string, typeof Camera> = { Photo: Camera, Video: Eye, Voice: Mic, Note: MessageSquare };

  return (
    <div className="max-w-lg mx-auto pb-8">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-card sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/train" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-primary uppercase tracking-wider">Assessment Result</p>
            <h1 className="text-sm font-bold text-foreground truncate">{data.module.displayLabel}</h1>
          </div>
          <Badge className={isApproved ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'}>
            {data.session.status}
          </Badge>
        </div>
      </div>

      {/* Assessor info */}
      <div className="px-4 py-3 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {data.assessor.name}</span>
          {data.session.completedAt && (
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {format(new Date(data.session.completedAt), 'dd MMM yyyy, h:mm a')}</span>
          )}
        </div>
        {data.session.employeeVisibleNotes && (
          <p className="text-sm text-foreground mt-2 bg-card p-3 rounded-lg border border-border">{data.session.employeeVisibleNotes}</p>
        )}
      </div>

      {/* Criteria outcomes */}
      <div className="px-4 py-4 space-y-2">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Assessment Criteria</h2>
        {data.criteria.map(c => {
          const evidence = data.evidence.filter(e => e.criterionIndex === c.index);
          return (
            <div key={c.index} className="p-3 border border-border rounded-xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground flex-1">{c.label}</span>
                <Badge variant={c.score === 'competent' ? 'default' : 'destructive'} className="text-[10px] uppercase">
                  {c.score === 'competent' ? '✓ Competent' : c.score === 'not_yet' ? '✗ Not Yet' : c.score}
                </Badge>
              </div>
              {evidence.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {evidence.map(ev => {
                    const Icon = typeIcons[ev.type] || FileText;
                    return (
                      <div key={ev.id} className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                        <Icon className="w-3 h-3" />
                        <span>{ev.type}</span>
                        {ev.fileUrl && (ev.type === 'Photo' || ev.type === 'Video') && (
                          <a href={ev.fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                            <Download className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {evidence.filter(e => e.caption).map(e => (
                <p key={`cap-${e.id}`} className="text-xs text-muted-foreground italic">"{e.caption}"</p>
              ))}
            </div>
          );
        })}
      </div>

      {/* Corrective Actions */}
      {data.correctiveActions.length > 0 && (
        <div className="px-4 py-3 border-t border-border">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Action Plan</h2>
          {data.correctiveActions.map(ca => (
            <div key={ca.id} className="p-3 bg-destructive/5 border border-destructive/20 rounded-xl mb-2">
              <p className="text-sm font-medium text-foreground">{ca.actionTitle}</p>
              {ca.actionsRequired && <p className="text-xs text-muted-foreground mt-1">Actions: {ca.actionsRequired}</p>}
              {ca.employeeVisibleNotes && <p className="text-xs text-muted-foreground mt-1">Notes: {ca.employeeVisibleNotes}</p>}
              {ca.reassessmentDate && <p className="text-xs text-muted-foreground mt-1">Reassessment: {format(new Date(ca.reassessmentDate), 'dd MMM yyyy')}</p>}
              <Badge variant="outline" className="mt-1.5 text-[10px]">{ca.status}</Badge>
            </div>
          ))}
        </div>
      )}

      <div className="px-4 pt-4">
        <Button variant="outline" className="w-full" asChild>
          <Link to="/train"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Training</Link>
        </Button>
      </div>
    </div>
  );
}
